"use client";

import dynamic from "next/dynamic";
import { useCallback, useState } from "react";
import PhosphorIcon from "@/shared/components/common/PhosphorIcon";
import type { HotelDetailV2Image } from "../model/hotelDetailV2.model";
import { DETAIL_V2_SAMPLE_OFFICIAL_IMAGES } from "../model/detailV2.samples";
import DetailImage from "./DetailImage";

const GalleryDialog = dynamic(() => import("./GalleryDialog"), {
  ssr: false,
});

interface HotelGalleryProps {
  hotelName: string;
  images: HotelDetailV2Image[];
}

export default function HotelGallery({
  hotelName,
  images,
}: HotelGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  const openDialog = useCallback((index: number) => {
    setActiveIndex(index);
    setDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => setDialogOpen(false), []);

  if (images.length === 0) {
    return (
      <section
        id="overview"
        className="grid h-60 place-items-center rounded-lg border border-[#e5e7eb] bg-white text-[#9ca3af] md:h-[420px]"
      >
        <div className="flex flex-col items-center gap-2">
          <PhosphorIcon name="Image" size={30} />
          <span className="text-sm">تصویری برای این هتل ثبت نشده است</span>
        </div>
      </section>
    );
  }

  const visibleThumbnails = images.slice(1, 5);
  const mobilePreviewImages = images.slice(1, 5);

  return (
    <>
      <section id="overview" aria-label={`تصاویر ${hotelName}`}>
        <div className="hidden h-[420px] gap-2 overflow-hidden rounded-lg md:flex">
          <button
            type="button"
            onClick={() => openDialog(0)}
            className="group relative w-[48%] overflow-hidden bg-slate-200"
            aria-label="باز کردن گالری تصاویر"
          >
            <DetailImage
              src={images[0].fullUrl ?? images[0].url}
              fallbackSrc={DETAIL_V2_SAMPLE_OFFICIAL_IMAGES[0]}
              alt={images[0].alt}
              fill
              priority
              quality={88}
              sizes="(max-width: 767px) 50vw, 560px"
              className="object-cover transition duration-500 group-hover:scale-[1.03]"
            />
            <span className="absolute inset-0 bg-black/0 transition group-hover:bg-black/10" />
          </button>

          {visibleThumbnails.length > 0 ? (
            <div className="grid flex-1 grid-cols-2 gap-2">
              {visibleThumbnails.map((image, index) => {
                const imageIndex = index + 1;
                const isLastVisible = index === visibleThumbnails.length - 1;

                return (
                  <button
                    key={image.id}
                    type="button"
                    onClick={() => openDialog(imageIndex)}
                    className="group relative min-h-0 overflow-hidden bg-slate-200"
                    aria-label={`نمایش تصویر ${imageIndex + 1}`}
                  >
                    <DetailImage
                      src={image.fullUrl ?? image.url}
                      fallbackSrc={
                        DETAIL_V2_SAMPLE_OFFICIAL_IMAGES[
                          imageIndex % DETAIL_V2_SAMPLE_OFFICIAL_IMAGES.length
                        ]
                      }
                      alt={image.alt}
                      fill
                      quality={82}
                      sizes="(max-width: 767px) 25vw, 260px"
                      className="object-cover transition duration-500 group-hover:scale-105"
                    />
                    {isLastVisible ? (
                      <span className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-black/55 text-white">
                        <PhosphorIcon name="Camera" size={24} />
                        <span className="text-sm font-bold">
                          مشاهده همه {images.length.toLocaleString("fa-IR")} تصویر
                        </span>
                      </span>
                    ) : (
                      <span className="absolute inset-0 bg-black/0 transition group-hover:bg-black/10" />
                    )}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className="grid h-48 grid-cols-2 gap-1.5 overflow-hidden rounded-lg md:hidden">
          <button
            type="button"
            onClick={() => openDialog(0)}
            className="group relative min-h-0 overflow-hidden bg-slate-200"
            aria-label="باز کردن گالری تصاویر"
          >
            <DetailImage
              src={images[0].fullUrl ?? images[0].url}
              fallbackSrc={DETAIL_V2_SAMPLE_OFFICIAL_IMAGES[0]}
              alt={images[0].alt}
              fill
              priority
              quality={88}
              sizes="50vw"
              className="object-cover transition duration-500 group-hover:scale-[1.03]"
            />
            <span className="absolute inset-0 bg-black/0 transition group-hover:bg-black/10" />
          </button>

          {mobilePreviewImages.length > 0 ? (
            <div className="grid min-h-0 grid-cols-2 grid-rows-2 gap-1.5">
              {mobilePreviewImages.map((image, index) => {
                const imageIndex = index + 1;
                const isLastVisible = index === mobilePreviewImages.length - 1;

                return (
                  <button
                    key={image.id}
                    type="button"
                    onClick={() => openDialog(imageIndex)}
                    className="group relative min-h-0 overflow-hidden bg-slate-200"
                    aria-label={`نمایش تصویر ${imageIndex + 1}`}
                  >
                    <DetailImage
                      src={image.fullUrl ?? image.url}
                      fallbackSrc={
                        DETAIL_V2_SAMPLE_OFFICIAL_IMAGES[
                          imageIndex % DETAIL_V2_SAMPLE_OFFICIAL_IMAGES.length
                        ]
                      }
                      alt={image.alt}
                      fill
                      quality={82}
                      sizes="25vw"
                      className="object-cover transition duration-500 group-hover:scale-105"
                    />
                    {isLastVisible ? (
                      <span className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-1.5 bg-black/55 text-white">
                        <PhosphorIcon name="Camera" size={18} />
                        <span className="px-1 text-center text-[11px] font-bold leading-5">
                          مشاهده همه تصاویر
                        </span>
                      </span>
                    ) : (
                      <span className="absolute inset-0 bg-black/0 transition group-hover:bg-black/10" />
                    )}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      </section>

      {dialogOpen ? (
        <GalleryDialog
          hotelName={hotelName}
          images={images}
          guestImages={[]}
          activeIndex={activeIndex}
          onActiveIndexChange={setActiveIndex}
          onClose={closeDialog}
        />
      ) : null}
    </>
  );
}
